"use client";

import { useEffect, useState } from "react";
import Api from "../../../config/Api";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { BsEyeFill } from "react-icons/bs";
import StatusBadge from "../Application/Components/StatusBadge";
import Loader from "../../Components/Widget/Loader";
import TooltipWidget from "../../Components/Widget/TooltipWidget";

const PostApprovalApplications = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [application, setApplication] = useState([]);
  const [isApplicationLoaded, setIsApplicationLoaded] = useState(false);
  const [filter, setFilter] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const getApplication = async (
    status_id,
    page = 1,
    search = "",
    pageSize = perPage
  ) => {
    try {
      setIsLoading(true);
      const body = {
        status_id: status_id,
        page_no: page,
        per_page: pageSize,
      };

      if (search.trim()) {
        body.search = search.trim();
      }

      const res = await Api("application_list", "POST", body, true);

      if (res.data.status === 1) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        const applicationData = Array.isArray(res.data.data) ? res.data.data : [];
        const paginationData =
          res.data.pagination &&
          typeof res.data.pagination === "object"
            ? res.data.pagination
            : res.data &&
              typeof res.data === "object" &&
              res.data.page
            ? {
                page_no: res.data.page,
                per_page: res.data.per_page,
                total_pages: res.data.total_pages,
                total_records: res.data.total,
              }
            : null;

        setApplication(applicationData);

        if (paginationData) {
          setCurrentPage(paginationData.page_no || page);
          setTotalPages(paginationData.total_pages || 1);
          setTotalRecords(paginationData.total_records || 0);
          setPerPage(paginationData.per_page || pageSize);
        } else {
          const total =
            res.data.total || res.data.count || applicationData.length;
          setCurrentPage(page);
          setTotalPages(Math.max(Math.ceil(total / pageSize), 1));
          setTotalRecords(total);
          setPerPage(pageSize);
        }

        setIsApplicationLoaded(true);
      } else {
        toast.error(res.data.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      toast.error("Something went wrong!", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchInput = (value) => {
    setFilter(value);

    if (value.trim() === "") {
      setCurrentPage(1);
      setResetPaginationToggle((prev) => !prev);
      getApplication([6, 7, 8, 9, 10, 11], 1, "", perPage);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getApplication([6, 7, 8, 9, 10, 11], 1, filter.trim(), perPage);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      getApplication([6, 7, 8, 9, 10, 11], page, filter, perPage);
    }
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
    getApplication([6, 7, 8, 9, 10, 11], 1, filter, newPerPage);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  useEffect(() => {
    getApplication([6, 7, 8, 9, 10, 11]);
  }, []);

  return (
    <>
      <div className="bg-white p-3 rounded shadow">
        <div className="mb-3">
          <div className="row g-2">
            <div className="col-lg-6 col-md-5">
              <h5 className="text-dark">Post Approval Applications</h5>
            </div>

            <div className="col-sm-6 col-lg-5">
              <input
                type="text"
                placeholder="Search Application"
                className="form-control"
                value={filter}
                onChange={(e) => handleSearchInput(e.target.value)}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </div>
            <div className="col-lg-1">
              <div>
                <div className="col-lg-2">
                  <div>
                    <button
                      className="btn btn-primary"
                      onClick={handleSearch}
                      disabled={isLoading}
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Info */}
        {isApplicationLoaded && (
          <div className="mb-3">
            <small className="text-muted">
              Showing {(currentPage - 1) * perPage + 1} to{" "}
              {Math.min(currentPage * perPage, totalRecords)} of {totalRecords}{" "}
              Post Approval Applications
            </small>
          </div>
        )}

        {/* Table */}
        <div className="table-responsive">
          {isApplicationLoaded ? (
            <>
              <table className="table table-striped table-hover">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Application ID</th>
                    <th>Applicant Passport Photo</th>
                    <th>Applicant Info</th>
                    <th>District</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {application.length > 0 ? (
                    application.map((row, index) => (
                      <tr key={row.id}>
                        <td>{(currentPage - 1) * perPage + index + 1}</td>
                        <td>{row.application_id}</td>
                        <td>
                          <img
                            src={
                              row.applicant_default_img || "/placeholder.svg"
                            }
                            alt="Applicant"
                            style={{
                              height: "70px",
                              width: "100px",
                              objectFit: "cover",
                            }}
                          />
                        </td>
                        <td>
                          <div className="mb-1">{row.applicant_name}</div>
                          <div>{row.mobile}</div>
                        </td>
                        <td>{row.district?.district_name}</td>
                        <td>
                          {row?.payment_status === "R" ? (
                            <>
                              <StatusBadge status={row.status} />
                              <span
                                style={{
                                  backgroundColor: "red",
                                  color: "white",
                                  padding: "4px 6px",
                                  borderRadius: "12px",
                                  fontSize: "11px",
                                }}
                              >
                                Failed
                              </span>
                            </>
                          ) : (
                            <StatusBadge status={row.status} />
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <TooltipWidget title="View SDRF">
                              <Link
                                className="btn btn-warning btn-sm"
                                to={`/application/${window.btoa(row.id)}`}
                                state={{ source: "post-approval" }}
                              >
                                <BsEyeFill />
                              </Link>
                            </TooltipWidget>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="text-muted">
                          <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                          No post approval applications found
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav aria-label="Page navigation">
                  <ul className="pagination justify-content-center">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {getPageNumbers().map((page, index) => (
                      <li
                        key={index}
                        className={`page-item ${
                          page === currentPage ? "active" : ""
                        } ${page === "..." ? "disabled" : ""}`}
                      >
                        {page === "..." ? (
                          <span className="page-link">...</span>
                        ) : (
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        )}
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          ) : (
            <Loader />
          )}
        </div>
      </div>
    </>
  );
};

export default PostApprovalApplications;
